import React, { useState } from 'react';
import axios from 'axios';
import './SignUp.css';
import { API_URL } from '../config/data';

const SignUp = () => {
  const [formData, setFormData] = useState({
    lastName: '',
    firstName: '',
    middleName: '',
    phone: '',
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const phoneConfig = {
    code: '+7',
    country: 'Россия',
    flag: '/assets/img/ru.svg',
    maxLength: 10,
    placeholder: '999 999 99 99'
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Очищаем ошибку при изменении поля
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };


  const handlePhoneChange = (e) => {
    let value = e.target.value;
    
    // Удаляем все нецифровые символы
    const digitsOnly = value.replace(/\D/g, '');
    
    // Ограничиваем длину для российского номера (10 цифр)
    const limitedDigits = digitsOnly.slice(0, phoneConfig.maxLength);
    
    // Применяем российскую маску: 999 999 99 99
    if (limitedDigits.length > 0) {
      value = limitedDigits.replace(/(\d{3})(\d{0,3})(\d{0,2})(\d{0,2})/, (match, p1, p2, p3, p4) => {
        let result = p1;
        if (p2) result += ` ${p2}`;
        if (p3) result += ` ${p3}`;
        if (p4) result += ` ${p4}`;
        return result;
      });
    } else {
      value = '';
    }
    
    setFormData(prev => ({
      ...prev,
      phone: value
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Введите фамилию';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'Введите имя';
    }

    if (!formData.middleName.trim()) {
      newErrors.middleName = 'Введите отчество';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Введите номер телефона';
    } else {
      const phoneDigits = formData.phone.replace(/\D/g, '');
      if (phoneDigits.length < phoneConfig.maxLength) {
        newErrors.phone = 'Введите полный номер телефона';
      }
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Введите email';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Введите корректный email';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Введите пароль';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Пароль должен содержать минимум 8 символов';
    } else {
      // Проверяем наличие заглавной буквы, строчной буквы и цифры
      const hasUpperCase = /[A-Z]/.test(formData.password);
      const hasLowerCase = /[a-z]/.test(formData.password);
      const hasNumber = /\d/.test(formData.password);
      
      if (!hasUpperCase || !hasLowerCase || !hasNumber) {
        newErrors.password = 'Пароль должен содержать минимум одну заглавную букву, одну строчную букву и одну цифру';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      // Подготавливаем данные для отправки
      const registrationData = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        middleName: formData.middleName,
        phone: `+7${formData.phone.replace(/\D/g, '')}` // Убираем пробелы и добавляем +7
      };

      // Отправляем запрос на регистрацию
      const response = await axios.post(`${API_URL}/api/auth/register`, registrationData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200 || response.status === 201) {
        setSuccessMessage('Регистрация прошла успешно! Теперь вы можете войти в систему.');
        
        // Очищаем форму после успешной регистрации
        setFormData({
          lastName: '',
          firstName: '',
          middleName: '',
          phone: '',
          email: '',
          password: ''
        });

        // Перенаправляем на страницу входа через 2 секунды
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
    } catch (error) {
      console.error('Ошибка при регистрации:', error);
      
      if (error.response) {
        // Ошибка от сервера
        const errorData = error.response.data;
        
        if (errorData.errors) {
          // Обрабатываем ошибки валидации
          const serverErrors = {};
          errorData.errors.forEach(err => {
            if (err.field === 'email') {
              serverErrors.email = err.message;
            } else if (err.field === 'phone') {
              serverErrors.phone = err.message;
            } else if (err.field === 'firstName') {
              serverErrors.firstName = err.message;
            } else if (err.field === 'lastName') {
              serverErrors.lastName = err.message;
            } else if (err.field === 'middleName') {
              serverErrors.middleName = err.message;
            } else if (err.field === 'password') {
              serverErrors.password = err.message;
            }
          });
          setErrors(serverErrors);
        } else if (errorData.message) {
          // Общая ошибка
          setErrors({ general: errorData.message });
        } else {
          setErrors({ general: 'Произошла ошибка при регистрации' });
        }
      } else if (error.request) {
        // Ошибка сети
        setErrors({ general: 'Ошибка сети. Проверьте подключение к интернету.' });
      } else {
        // Другая ошибка
        setErrors({ general: 'Произошла непредвиденная ошибка' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="wrapper">
      <header data-fls-header-login="" data-fls-header-scroll="" className="header-login">
        <div className="header-login__container">
          <div className="header-login__body">
            <a href="/" className="header-login__logo">
              <img src="/assets/img/logo.svg" alt="Logo" />
            </a>
          </div>
        </div>
      </header>
      <main className="page auth">
        <div className="auth__container">
          <div className="auth__body signup">
            <form data-fls-form="" action="#" method="#" data-fls-block="" className="signup__content" onSubmit={handleSubmit}>
              <h4 className="signup__title signup__title--title-25">Регистрация</h4>
              
              {/* Сообщение об успехе */}
              {successMessage && (
                <div className="signup__success">
                  {successMessage}
                </div>
              )}
              
              {/* Общая ошибка */}
              {errors.general && (
                <div className="signup__error-general">
                  {errors.general}
                </div>
              )}
              
              {/* Фамилия */}
              <div className="signup__field">
                <input 
                  required 
                  data-fls-input="" 
                  autoComplete="off" 
                  type="text" 
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder="Ваша фамилия" 
                  className={`input ${errors.lastName ? '--form-error' : ''}`}
                />
                {errors.lastName && <div className="signup__error">{errors.lastName}</div>}
              </div>

              {/* Имя */}
              <div className="signup__field">
                <input 
                  required 
                  data-fls-input="" 
                  autoComplete="off" 
                  type="text" 
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder="Ваше имя" 
                  className={`input ${errors.firstName ? '--form-error' : ''}`}
                />
                {errors.firstName && <div className="signup__error">{errors.firstName}</div>}
              </div>

              {/* Отчество */}
              <div className="signup__field">
                <input 
                  required 
                  data-fls-input="" 
                  autoComplete="off" 
                  type="text" 
                  name="middleName"
                  value={formData.middleName}
                  onChange={handleInputChange}
                  placeholder="Ваше отчество" 
                  className={`input ${errors.middleName ? '--form-error' : ''}`}
                />
                {errors.middleName && <div className="signup__error">{errors.middleName}</div>}
              </div>

              {/* Номер телефона */}
              <div className="signup__field">
                <div className={`signup__phone-container ${errors.phone ? '--form-error' : ''}`}>
                  <div className="signup__phone-flag">
                    <img src={phoneConfig.flag} alt="Flag" />
                  </div>
                  <div className="signup__phone-code">
                    <span className="signup__country-code">{phoneConfig.code}</span>
                  </div>
                  <input 
                    required 
                    data-fls-input="" 
                    data-fls-input-mask="+7 (999) 999 - 99 - 99" 
                    autoComplete="off" 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    placeholder={phoneConfig.placeholder} 
                    className="signup__phone-input"
                  />
                </div>
                {errors.phone && <div className="signup__error">{errors.phone}</div>}
              </div>

              {/* Email */}
              <div className="signup__field">
                <input 
                  required 
                  data-fls-input="" 
                  autoComplete="off" 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Ваш email" 
                  className={`input ${errors.email ? '--form-error' : ''}`}
                />
                {errors.email && <div className="signup__error">{errors.email}</div>}
              </div>

              {/* Пароль */}
              <div className="signup__field">
                <div className="password">
                  <input 
                    required 
                    data-fls-input="" 
                    autoComplete="off" 
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="Пароль" 
                    className={`input ${errors.password ? '--form-error' : ''}`}
                  />
                  <button 
                    type="button"
                    className={`password__eye ${showPassword ? 'active' : ''}`}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    👁
                  </button>
                </div>
                {errors.password && <div className="signup__error">{errors.password}</div>}
                <div className="signup__password-hint">
                  Пароль должен содержать минимум 8 символов, одну заглавную букву, одну строчную букву и одну цифру
                </div>
              </div>

              {/* Текст соглашения */}
              <div className="signup__agreement">
                <p className="signup__agreement-text">
                  Нажимая кнопку «Регистрация», вы соглашаетесь с условиями использования личного кабинета и Персональных данных и с Ключевым информационным документом продаж.
                </p>
              </div>

              <button 
                type="submit" 
                data-fls-button="" 
                className="signup__button button button--fw"
                disabled={isLoading}
              >
                {isLoading ? 'Регистрируем...' : 'Регистрация'}
              </button>
            </form>
            <a href="/login" className="signup__login">Уже есть аккаунт? Войти</a>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SignUp;